import { Page, TabLink } from '@app-builder/components';
import { BreadCrumbLink, type BreadCrumbProps, BreadCrumbs } from '@app-builder/components/Breadcrumbs';
import { CaseStatusBadgeV2, casesI18n } from '@app-builder/components/Cases';
import { UploadFile } from '@app-builder/components/Files/UploadFile';
import { ScreeningStatusTag } from '@app-builder/components/Screenings/ScreeningStatusTag';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { isForbiddenHttpError, isNotFoundHttpError } from '@app-builder/models';
import { useUploadScreeningFile } from '@app-builder/queries/upload-screening-file';
import { fromParams, fromUUIDtoSUUID } from '@app-builder/utils/short-uuid';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { useTranslation } from 'react-i18next';
import { Button } from 'ui-design-system';
import { Icon } from 'ui-icons';

const screeningLayoutLoader = createServerFn()
  .middleware([authMiddleware])
  .inputValidator((input: { params?: Record<string, string> } | undefined) => input)
  .handler(async function screeningLayoutLoader({ context, data }) {
    const { user, entitlements, cases, dataModelRepository, inbox, screening: screeningRepository } = context.authInfo;

    const caseId = fromParams(data?.params ?? {}, 'caseId');
    const decisionId = fromParams(data?.params ?? {}, 'decisionId');
    const screeningId = fromParams(data?.params ?? {}, 'screeningId');

    try {
      const caseDetail = await cases.getCase({ caseId });
      const decision = caseDetail.decisions.find((d) => d.id === decisionId);
      const screenings = await screeningRepository.listScreenings({ decisionId });
      const currentInbox = await inbox.getInbox(caseDetail.inboxId);
      const screening = screenings.find((s) => s.id === screeningId);

      if (!decision || !screening) {
        throw new Response(null, { status: 404, statusText: 'Not Found' });
      }

      return {
        inbox: currentInbox,
        caseDetail,
        decision,
        user,
        entitlements,
        screening,
        dataModel: await dataModelRepository.getDataModel(),
        pivots: await dataModelRepository.listPivots({}),
      };
    } catch (error) {
      // On purpose catch 403 errors to display a 404 page
      if (isNotFoundHttpError(error) || isForbiddenHttpError(error)) {
        throw new Response(null, { status: 404, statusText: 'Not Found' });
      } else {
        throw error;
      }
    }
  });

export const Route = createFileRoute('/_app/_builder/cases/$caseId/d/$decisionId/screenings/$screeningId')({
  staticData: {
    BreadCrumbs: [
      ({ isLast }: BreadCrumbProps) => {
        const { t } = useTranslation(['navigation']);

        return (
          <BreadCrumbLink to="/cases" isLast={isLast}>
            <Icon icon="case-manager" className="me-sm size-6" />
            {t('navigation:case_manager')}
          </BreadCrumbLink>
        );
      },
      ({ isLast }: BreadCrumbProps) => {
        const { inbox } = Route.useLoaderData();

        return (
          <BreadCrumbLink to="/cases/inboxes/$inboxId" params={{ inboxId: fromUUIDtoSUUID(inbox.id) }} isLast={isLast}>
            {inbox.name}
          </BreadCrumbLink>
        );
      },
      ({ isLast }: BreadCrumbProps) => {
        const { t } = useTranslation(['cases']);
        const { caseDetail } = Route.useLoaderData();

        return (
          <div className="flex items-center gap-sm">
            <BreadCrumbLink to="/cases/$caseId" params={{ caseId: fromUUIDtoSUUID(caseDetail.id) }} isLast={isLast}>
              <span className="line-clamp-2 text-start">{t('cases:case.page_title')}</span>
            </BreadCrumbLink>
            <span className="text-s border-grey-border text-grey-secondary inline-flex gap-sm rounded-sm border px-xs font-normal">
              <span className="font-medium">ID</span>
              <span className="text-rtl max-w-20 truncate">{caseDetail.id}</span>
            </span>
            <CaseStatusBadgeV2 status={caseDetail.status} outcome={caseDetail.outcome} variant="semi-full" />
          </div>
        );
      },
      ({ isLast }: BreadCrumbProps) => {
        const { caseDetail, decision, screening } = Route.useLoaderData();

        return (
          <div className="flex items-center gap-sm">
            <BreadCrumbLink
              to="/cases/$caseId/d/$decisionId/screenings/$screeningId"
              params={{
                caseId: fromUUIDtoSUUID(caseDetail.id),
                decisionId: fromUUIDtoSUUID(decision.id),
                screeningId: fromUUIDtoSUUID(screening.id),
              }}
              isLast={isLast}
            >
              <span className="line-clamp-2 text-start">{screening.config.name}</span>
            </BreadCrumbLink>
            <ScreeningStatusTag
              status={screening.status}
              pendingHitCount={screening.matches.filter((m) => m.status === 'pending').length}
            />
          </div>
        );
      },
    ],
  },
  loader: ({ params }) => screeningLayoutLoader({ data: { params } }),
  component: CaseSanctionReviewPage,
});

function CaseSanctionReviewPage() {
  const { t } = useTranslation([...casesI18n, 'common', 'navigation']);
  const { caseDetail, screening } = Route.useLoaderData();
  const { mutateAsync: uploadScreeningFile } = useUploadScreeningFile(screening.id);

  return (
    <Page.Main>
      <Page.Header className="justify-between gap-xl">
        <BreadCrumbs back={`/cases/${fromUUIDtoSUUID(caseDetail.id)}`} />
      </Page.Header>
      <div className="flex size-full flex-col overflow-hidden">
        <Page.Container>
          <Page.Content width="readable">
            <nav>
              <ul className="bg-surface-card border-grey-border inline-flex flex-row gap-sm rounded-lg border p-xs">
                <li>
                  <TabLink
                    labelTKey="navigation:case_manager.hits"
                    to="./hits"
                    Icon={(props) => <Icon {...props} icon="tip" />}
                  />
                </li>
                <li>
                  <TabLink
                    labelTKey="navigation:case_manager.files"
                    to="./files"
                    Icon={(props) => <Icon {...props} icon="attachment" />}
                  />
                </li>
              </ul>
            </nav>
            <Outlet />
          </Page.Content>
        </Page.Container>
        <div className="bg-surface-card border-t-grey-border flex shrink-0 flex-row items-center justify-end gap-md border-t p-md">
          <UploadFile uploadFileEndpoint={uploadScreeningFile}>
            <Button className="h-14 w-fit whitespace-nowrap" variant="secondary">
              <Icon icon="attachment" className="size-5" />
              {t('cases:add_file')}
            </Button>
          </UploadFile>
        </div>
      </div>
    </Page.Main>
  );
}
